import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { PlayCircle, Search, Calendar, Radio, ExternalLink, Facebook } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { church } from "@/config/church";
import StockPhotoNotice from "@/components/StockPhotoNotice";

export default function Media() {
  useReveal();
  const [sermons, setSermons] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [series, setSeries] = useState("All");
  const [speaker, setSpeaker] = useState("All");
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 6;

  useEffect(() => {
    api
      .get("/sermons")
      .then((r) => {
        setSermons(r.data || []);
        setSelected((r.data || [])[0] || null);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const seriesList = useMemo(
    () => ["All", ...Array.from(new Set(sermons.map((s) => s.series).filter(Boolean)))],
    [sermons]
  );
  const speakerList = useMemo(
    () => ["All", ...Array.from(new Set(sermons.map((s) => s.speaker).filter(Boolean)))],
    [sermons]
  );

  const filtered = sermons.filter((s) => {
    const matchesQ =
      !q ||
      `${s.title || ""} ${s.scripture || ""} ${s.description || ""}`
        .toLowerCase()
        .includes(q.toLowerCase());
    const matchesSeries = series === "All" || s.series === series;
    const matchesSpeaker = speaker === "All" || s.speaker === speaker;
    return matchesQ && matchesSeries && matchesSpeaker;
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const isEmpty = loaded && sermons.length === 0;

  return (
    <div data-testid="media-page">
      <PageHero
        eyebrow="Media & Sermons"
        title="Feed your faith."
        subtitle="Watch live every Sunday and revisit anointed messages from Pastor Darren M. Little and the New Philadelphia teaching team."
        image="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/bf7g84zx_IMG_5577.jpg"
      />

      {/* Featured / Watch Live */}
      <section className="bg-npbc-black text-white py-20">
        <div className="container-npbc grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
          <div
            data-reveal
            className="relative rounded-3xl overflow-hidden aspect-video shadow-2xl border border-white/10 bg-gradient-to-br from-npbc-red/25 via-npbc-black to-npbc-gold/15"
          >
            <div className="absolute inset-0 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:4px_4px] opacity-40" />
            <div className="absolute inset-0 grid place-items-center">
              <a
                href={church.social.facebookLive}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="media-watch-live-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-white/95 pl-4 pr-6 py-3 text-npbc-black font-semibold shadow-2xl hover:scale-105 transition"
              >
                <span className="grid h-10 w-10 place-items-center rounded-full bg-npbc-red text-white">
                  <PlayCircle className="h-5 w-5" />
                </span>
                Watch Live on Facebook
              </a>
            </div>
            <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-black/50 backdrop-blur px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-npbc-gold">
              <span className="h-1.5 w-1.5 rounded-full bg-npbc-red animate-pulse" />
              Live · Every Sunday · 11 AM
            </div>
          </div>
          <div data-reveal>
            <div className="eyebrow !text-npbc-gold">Watch Live</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              Every Sunday at 11:00 AM.
            </h2>
            <p className="mt-4 text-white/75">
              Can't make it in person? Join us on Facebook Live every Sunday morning — sing, hear the
              Word, and be part of the family from wherever you are.
            </p>
            <a
              href={church.social.facebookLive}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="media-facebook-btn"
              className="btn-primary mt-8"
            >
              <Facebook className="h-4 w-4" /> Follow on Facebook{" "}
              <ExternalLink className="h-4 w-4 opacity-70" />
            </a>
          </div>
        </div>
      </section>

      {/* Sermons list — real ones only */}
      <section className="bg-white py-20">
        <div className="container-npbc">
          {isEmpty ? (
            <div className="text-center max-w-xl mx-auto" data-reveal>
              <div className="eyebrow justify-center flex">Sermon Archive</div>
              <h2 className="mt-3 heading-serif text-4xl md:text-5xl leading-tight">
                More sermons coming soon.
              </h2>
              <p className="mt-4 text-npbc-gray/85 leading-relaxed">
                Every Sunday message will be posted here shortly after it airs live. In the meantime,
                catch every service on our Facebook page.
              </p>
              <a
                href={church.social.facebookLive}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="media-empty-facebook-btn"
                className="btn-primary mt-8"
              >
                <Facebook className="h-4 w-4" /> Visit our Facebook{" "}
                <ExternalLink className="h-4 w-4 opacity-70" />
              </a>
            </div>
          ) : (
            <>
              <SectionHeader
                eyebrow="Sermon Archive"
                title="Recent messages"
                subtitle="Filter by series or speaker to find a message that meets you where you are."
              />

              <div
                className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-3 mt-10 mb-10"
                data-reveal
              >
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-npbc-gray/60" />
                  <input
                    data-testid="media-search"
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search title, scripture, or topic"
                    className="w-full pl-11 pr-4 py-3 rounded-full border border-black/10 bg-white outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20 transition"
                  />
                </div>
                <select
                  value={series}
                  onChange={(e) => {
                    setSeries(e.target.value);
                    setPage(1);
                  }}
                  data-testid="media-series-filter"
                  className="px-4 py-3 rounded-full border border-black/10 bg-white outline-none focus:border-npbc-red"
                >
                  {seriesList.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <select
                  value={speaker}
                  onChange={(e) => {
                    setSpeaker(e.target.value);
                    setPage(1);
                  }}
                  data-testid="media-speaker-filter"
                  className="px-4 py-3 rounded-full border border-black/10 bg-white outline-none focus:border-npbc-red"
                >
                  {speakerList.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {paged.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    data-reveal
                    onClick={() => {
                      setSelected(s);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    data-testid={`sermon-card-${s.id}`}
                    style={{ transitionDelay: `${(i % 6) * 50}ms` }}
                    className="group text-left rounded-2xl overflow-hidden bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_20px_60px_-30px_rgba(0,0,0,0.2)] hover:-translate-y-1 transition-all duration-500"
                  >
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-npbc-red/15 via-npbc-cream to-npbc-gold/10">
                      {s.thumbnail && (
                        <img
                          src={s.thumbnail}
                          alt={s.title}
                          className="h-full w-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute inset-0 grid place-items-center">
                        <span className="grid h-12 w-12 place-items-center rounded-full bg-white/95 text-npbc-red shadow-xl group-hover:scale-110 transition">
                          <PlayCircle className="h-6 w-6" />
                        </span>
                      </div>
                      {s.duration && (
                        <div className="absolute bottom-3 right-3 text-[10px] font-bold uppercase tracking-[0.22em] bg-black/60 backdrop-blur text-white px-2 py-1 rounded-full">
                          {s.duration}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      {s.series && (
                        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-npbc-red">
                          {s.series}
                        </div>
                      )}
                      <h3 className="mt-1 heading-serif text-xl leading-snug">{s.title}</h3>
                      <div className="mt-3 flex items-center gap-4 text-xs text-npbc-gray/70">
                        {s.date && (
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" /> {s.date}
                          </span>
                        )}
                        {s.speaker && <span>{s.speaker}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {pageCount > 1 && (
                <div className="mt-12 flex justify-center gap-2">
                  {Array.from({ length: pageCount }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`h-10 w-10 rounded-full font-semibold text-sm transition ${
                        page === i + 1
                          ? "bg-npbc-red text-white"
                          : "bg-npbc-cream text-npbc-black hover:bg-npbc-black hover:text-white"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <StockPhotoNotice />
    </div>
  );
}
